import { Fragment, useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetFieldTesterTenure } from 'api/kpi/kpi';
import { GroupingLevel } from 'api/model';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useExpandedRows from './useExpandedRows';
import periodLabel from './periodLabel';
import {
  ControlFigures,
  nf,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

/** Dagar som månader — anställningstid läses i månader, inte i hundratals dagar. */
const months = (days: number | null | undefined) =>
  days === null || days === undefined ? '–' : `${nf(days / 30.44, 1)} mån`;

/** ZäkraGo gick live 2022; anställningstiden räknas alltid därifrån. */
const tenureFrom = new Date(2022, 0, 1);

/**
 * Fälttestarnas anställningstid per hemlän: första till sista uppdraget för anställningar som
 * avslutats sedan 2022, och pågående anställningar som jämförelse. Datumoberoende — dashboardens
 * period och gruppering används inte (snittet för en dag eller vecka säger inget), bara länsfiltren.
 * Se OperationsKpiLogic.GetFieldTesterTenure.
 */
const FieldTesterTenureWidget = () => {
  const {
    dashboardState: { selectedFromCountyIDs, selectedToCountyIDs },
  } = useDashboardControls();
  const getData = useKPIGetFieldTesterTenure();
  const expanded = useExpandedRows();
  useEffect(() => {
    getData.mutate({
      data: {
        startDate: tenureFrom,
        endDate: new Date(),
        fromCountyIDs: selectedFromCountyIDs,
        toCountyIDs: selectedToCountyIDs,
        groupBy: GroupingLevel.Year,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFromCountyIDs, selectedToCountyIDs]);

  const result = getData.data;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.FieldTesterTenure]}
      count={result?.ended}
      isLoading={getData.isLoading}
      info="Anställningstid = första till sista slutförda uppdraget. Gäller alltid 2022 (då ZäkraGo gick live) till idag — dashboardens period påverkar inte widgeten, bara länsfiltret. Avslutade = fälttestare med status Avslutad. Pågående = inte avslutade med uppdrag de senaste 60 dagarna, tid räknad till idag. Län = hemlän; för avslutade är postnumret ofta rensat, och då tas länet ur ändringsloggen eller uppskattas ur var arbetsdagarna började. Klicka på ett hemlän för att se dess avslutade per år."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={false}
        isEmpty={!result || result.ended + result.ongoing === 0}
      >
        {result && !result.fieldTesterRoleResolved ? (
          <p>Rollen Fälttestare hittades inte — måttet går inte att räkna.</p>
        ) : (
          <>
            <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
              <strong>{months(result?.endedMedianDays)}</strong> median för{' '}
              {nf(result?.ended ?? 0)} avslutade (snitt{' '}
              {months(result?.endedAverageDays)}) · {nf(result?.ongoing ?? 0)}{' '}
              pågående, median hittills {months(result?.ongoingMedianDays)}
            </p>

            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>År (sista uppdrag)</th>
                  <th style={thNumStyle}>Avslutade</th>
                  <th style={thNumStyle}>Median</th>
                  <th style={thNumStyle}>Snitt</th>
                </tr>
              </thead>
              <tbody>
                {(result?.dataPoints ?? []).map((row) => (
                  <tr
                    key={new Date(row.date).toISOString()}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={tdStyle}>
                      {periodLabel(row.date, GroupingLevel.Year)}
                    </td>
                    <td style={tdNumStyle}>{nf(row.ended)}</td>
                    <td style={tdNumStyle}>{months(row.endedMedianDays)}</td>
                    <td style={tdNumStyle}>{months(row.endedAverageDays)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <table style={{ ...tableStyle, marginTop: '16px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Hemlän</th>
                  <th style={thNumStyle}>Avslutade</th>
                  <th style={thNumStyle}>Median</th>
                  <th style={thNumStyle}>Snitt</th>
                  <th style={thNumStyle}>Pågående</th>
                  <th style={thNumStyle}>Median hittills</th>
                </tr>
              </thead>
              <tbody>
                {(result?.byCounty ?? []).map((row) => {
                  const canExpand = row.byYear.length > 0;
                  const isOpen = canExpand && expanded.isOpen(row.countyName);
                  return (
                    <Fragment key={row.countyName}>
                      <tr
                        style={{
                          borderBottom: '1px solid #eee',
                          cursor: canExpand ? 'pointer' : undefined,
                        }}
                        onClick={
                          canExpand
                            ? () => expanded.toggle(row.countyName)
                            : undefined
                        }
                      >
                        <td style={tdStyle}>
                          {canExpand && (isOpen ? '▾ ' : '▸ ')}
                          {row.countyName}
                        </td>
                        <td style={tdNumStyle}>{nf(row.ended)}</td>
                        <td style={tdNumStyle}>
                          {months(row.endedMedianDays)}
                        </td>
                        <td style={tdNumStyle}>
                          {months(row.endedAverageDays)}
                        </td>
                        <td style={tdNumStyle}>{nf(row.ongoing)}</td>
                        <td style={tdNumStyle}>
                          {months(row.ongoingMedianDays)}
                        </td>
                      </tr>
                      {isOpen &&
                        row.byYear.map((year) => (
                          <tr
                            key={`${row.countyName}-${new Date(year.date).toISOString()}`}
                            style={{
                              borderBottom: '1px solid #f3f3f3',
                              color: '#555',
                            }}
                          >
                            <td style={{ ...tdStyle, paddingLeft: '28px' }}>
                              {periodLabel(year.date, GroupingLevel.Year)}
                            </td>
                            <td style={tdNumStyle}>{nf(year.ended)}</td>
                            <td style={tdNumStyle}>
                              {months(year.endedMedianDays)}
                            </td>
                            <td style={tdNumStyle}>
                              {months(year.endedAverageDays)}
                            </td>
                            <td style={tdNumStyle}>–</td>
                            <td style={tdNumStyle}>–</td>
                          </tr>
                        ))}
                    </Fragment>
                  );
                })}
                <tr style={totalRowStyle}>
                  <td style={tdStyle}>Totalt</td>
                  <td style={tdNumStyle}>{nf(result?.ended ?? 0)}</td>
                  <td style={tdNumStyle}>{months(result?.endedMedianDays)}</td>
                  <td style={tdNumStyle}>{months(result?.endedAverageDays)}</td>
                  <td style={tdNumStyle}>{nf(result?.ongoing ?? 0)}</td>
                  <td style={tdNumStyle}>
                    {months(result?.ongoingMedianDays)}
                  </td>
                </tr>
              </tbody>
            </table>

            <ControlFigures
              items={[
                result?.homeCountyEstimatedFromWork
                  ? `hemlän uppskattat ur arbetsdagarnas startlän för ${nf(result.homeCountyEstimatedFromWork)}`
                  : null,
                result?.homeCountyFromChangeLog
                  ? `hemlän ur ändringsloggen för ${nf(result.homeCountyFromChangeLog)}`
                  : null,
                result?.endedStartedAtSystemStart
                  ? `${nf(result.endedStartedAtSystemStart)} avslutade började vid systemstart och räknas inte`
                  : null,
                `${nf(result?.dormant ?? 0)} vilande (anställda utan uppdrag > ${result?.dormantDays ?? 60} dagar)`,
                `${nf(result?.dismissedWithoutJobs ?? 0)} avslutade körde aldrig något uppdrag (alla tider)`,
                result?.fieldTestersWithoutHomeCounty
                  ? `${nf(result.fieldTestersWithoutHomeCounty)} utan hemlän`
                  : null,
              ]}
            />
          </>
        )}
      </WidgetState>
    </Widget>
  );
};

export default FieldTesterTenureWidget;
